/**
 * 🤖 AUTO-GENERATED API SCHEMA FILE - DO NOT EDIT DIRECTLY!
 *
 * Generated from: performance_events entity configuration
 * Generator: scripts/generate-api-clients.ts
 *
 * This file contains the API client for the performance_events table with:
 * - Type-safe CRUD operations
 * - Field-level security enforcement
 * - Search functionality with multi-term search and global config integration
 * - Validation using generated schemas
 *
 * @description
 * This client provides a complete interface for interacting with the performance_events API.
 * All operations respect the field permissions defined in the entity configuration.
 *
 * Public fields: [id, performance_id, event_id, created_by, created_at, updated_by, updated_at, max_contestants, slack_run_status, slack_run_decided_at, slack_run_decided_by, slack_no_run_target_performance_id, slack_run_notes, is_scoring_published_live, scoring_published_live_at, scoring_published_live_by]
 * Searchable fields: []
 * Filterable fields: [id, performance_id, event_id, slack_no_run_target_performance_id]
 */

import { httpClient } from '../http-client.js';
import type { UnifiedQueryOptions } from '@config/search/global-search-config';
import type {
  PerformanceEventsRow,
  PerformanceEventsInsert,
  PerformanceEventsUpdate
} from '../../types/generated_schemas/generated-performance_events-schema.js';

export interface PerformanceEventsQueryOptions extends UnifiedQueryOptions {}

export interface PerformanceEventsResponse {
  data: PerformanceEventsRow[];
  count?: number;
  success: boolean;
}

/**
 * API Client for performance_events table operations
 *
 * @description Provides type-safe access to performance_events API endpoints with
 * field-level security enforcement and comprehensive error handling.
 */
export class PerformanceEventsClient {
  private readonly basePath = '/performance-events';

  /**
   * Get all performance_events records with optional filtering and pagination
   *
   * @param options - Query options for filtering, sorting, and pagination
   * @returns Promise resolving to array of performance_events records
   */
  async getAll(options?: PerformanceEventsQueryOptions): Promise<PerformanceEventsRow[]> {
    const params = new URLSearchParams();
    
    if (options?.limit) params.set('limit', options.limit.toString());
    if (options?.offset) params.set('offset', options.offset.toString());
    if (options?.sortBy) params.set('sortBy', options.sortBy);
    if (options?.sortOrder) params.set('sortOrder', options.sortOrder);
    if (options?.filters) {
      Object.entries(options.filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}?${queryString}` : this.basePath;
    
    const response = await httpClient.get<PerformanceEventsResponse>(url);
    return response.data;
  }

  /**
   * Get a single performance_events record by ID
   *
   * @param id - Unique identifier for the record
   * @returns Promise resolving to the performance_events record
   */
  async getById(id: string): Promise<PerformanceEventsRow> {
    const response = await httpClient.get<{
      data: PerformanceEventsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`);
    return response.data;
  }

  /**
   * Create a new performance_events record
   *
   * @param data - Data for creating the new record
   * @returns Promise resolving to the created performance_events record
   */
  async create(data: PerformanceEventsInsert): Promise<PerformanceEventsRow> {
    const response = await httpClient.post<{
      data: PerformanceEventsRow;
      success: boolean;
    }>(this.basePath, data);
    return response.data;
  }

  /**
   * Update an existing performance_events record
   *
   * @param id - Unique identifier for the record to update
   * @param data - Updated data for the record
   * @returns Promise resolving to the updated performance_events record
   */
  async update(id: string, data: PerformanceEventsUpdate): Promise<PerformanceEventsRow> {
    const response = await httpClient.put<{
      data: PerformanceEventsRow;
      success: boolean;
    }>(`${this.basePath}/${id}`, data);
    return response.data;
  }

  /**
   * Delete a performance_events record
   *
   * @param id - Unique identifier for the record to delete
   * @returns Promise resolving when deletion is complete
   */
  async delete(id: string): Promise<void> {
    await httpClient.delete(`${this.basePath}/${id}`);
  }

  /**
   * Create multiple performance_events records in a single request
   *
   * @param data - Array of data for creating new records
   * @returns Promise resolving to array of created performance_events records
   */
  async createMany(data: PerformanceEventsInsert[]): Promise<PerformanceEventsRow[]> {
    const response = await httpClient.post<{
      data: PerformanceEventsRow[];
      success: boolean;
    }>(`${this.basePath}/batch`, { items: data });
    return response.data;
  }

  /**
   * Delete multiple performance_events records
   *
   * @param ids - Array of unique identifiers for records to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteMany(ids: string[]): Promise<void> {
    await httpClient.delete(`${this.basePath}/batch`, { ids });
  }

  /**
   * Search performance_events with text query
   * ⚠️  SEARCH DISABLED: No fields marked as searchable in entity config
   */
  async search(
    _query: string,
    _columns?: string[],
    _options?: PerformanceEventsQueryOptions
  ): Promise<PerformanceEventsResponse> {
    throw new Error('Search operations are not available for performance_events - no searchable fields configured');
  }

  /**
   * Get count of performance_events records matching optional filters
   *
   * @param filters - Optional filters to apply when counting
   * @returns Promise resolving to the count of matching records
   */
  async count(filters?: Record<string, any>): Promise<number> {
    const params = new URLSearchParams();
    
    if (filters) {
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          params.set(key, value.toString());
        }
      });
    }

    const queryString = params.toString();
    const url = queryString ? `${this.basePath}/count?${queryString}` : `${this.basePath}/count`;
    
    const response = await httpClient.get<{ data: number; success: boolean }>(url);
    return response.data;
  }

  /**
   * Check if a performance_events record exists by ID
   *
   * @param id - Unique identifier to check
   * @returns Promise resolving to boolean indicating existence
   */
  async exists(id: string): Promise<boolean> {
    try {
      await this.getById(id);
      return true;
    } catch (error) {
      // Assuming 404 means record doesn't exist
      return false;
    }
  }

  /**
   * Get field configuration metadata for this entity
   *
   * @returns Object containing field permission and configuration data
   */
  getFieldConfig() {
    return {
      tableName: 'performance_events',
      publicFields: ['id', 'performance_id', 'event_id', 'created_by', 'created_at', 'updated_by', 'updated_at', 'max_contestants', 'slack_run_status', 'slack_run_decided_at', 'slack_run_decided_by', 'slack_no_run_target_performance_id', 'slack_run_notes', 'is_scoring_published_live', 'scoring_published_live_at', 'scoring_published_live_by'],
      searchableFields: [],
      filterableFields: ['id', 'performance_id', 'event_id', 'slack_no_run_target_performance_id'],
      sortableFields: ['created_at', 'updated_at'],
      readonlyFields: ['id', 'created_at', 'updated_at'],
      requiredFields: ['id', 'slack_run_status', 'is_scoring_published_live'],
    };
  }
}

// Export singleton instance for convenience
export const performanceEventsClient = new PerformanceEventsClient();
